/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Else, If, Then } from "react-if";
import styled from "styled-components";
import {
  SVGIcon,
  Box,
  theme,
  ExtendedTooltip,
  Text,
  SVGIconProps,
} from "@nordcloud/gnui";
import { CloudResourceProvider, ResourceStatus } from "~/generated/graphql";
import {
  getResourceColorContext,
  getResourceStatusIconName,
  getResourceStatusMessage,
} from "~/views/resources/utils";
import { getProviderIcon } from "~/views/resources_v2/utils";

type Props = {
  provider?: CloudResourceProvider;
  status?: ResourceStatus;
  iconSize?: SVGIconProps["size"];
  boxes?: boolean;
};

export function ResourceDetailsStatus({
  provider,
  status,
  iconSize,
  boxes,
}: Props) {
  const toolTipText = provider || "No Data Available";
  const providerIcon = provider
    ? getProviderIcon(provider)
    : "cloudQuestionMark";

  return (
    <>
      <If condition={status !== undefined}>
        <Then>
          <Wrap>
            <ExtendedTooltip
              status={getResourceColorContext(status as ResourceStatus)}
              caption={
                <Text tag="span" size="sm" color={theme.color.text.text04}>
                  {getResourceStatusMessage(status as ResourceStatus)}
                </Text>
              }
              position="start"
            >
              <IconBox
                icon={getResourceStatusIconName(status as ResourceStatus)}
                iconSize={iconSize}
                color={getResourceColorContext(status as ResourceStatus)}
                boxEnabled={boxes}
              />
            </ExtendedTooltip>
          </Wrap>
        </Then>
      </If>
      <Wrap>
        <ExtendedTooltip caption={toolTipText} position="start">
          <IconBox icon={providerIcon} iconSize={iconSize} boxEnabled={boxes} />
        </ExtendedTooltip>
      </Wrap>
    </>
  );
}

type IconBoxProps = {
  icon: SVGIconProps["name"];
  iconSize: SVGIconProps["size"];
  color?: SVGIconProps["color"];
  boxEnabled?: boolean;
};

function IconBox({ icon, iconSize, color, boxEnabled }: IconBoxProps) {
  const svgIcon = <SVGIcon size={iconSize ?? "lg"} name={icon} color={color} />;

  return (
    <If condition={boxEnabled}>
      <Then>
        <Box boxStyle="lightGrey" mb={theme.spacing.spacing04}>
          {svgIcon}
        </Box>
      </Then>
      <Else>{svgIcon}</Else>
    </If>
  );
}

const Wrap = styled.div`
  margin-right: ${theme.spacing.spacing03};
`;
